import React from 'react';
import { Check, Loader2, GitBranch } from 'lucide-react';

/**
 * WorkflowProgressIndicator — Shows real-time progress as Able builds a workflow.
 * Step indicators with checkmarks on completed steps and spinner on active step.
 *
 * ABLE-30: WorkflowProgressIndicator chat component
 */

const STEP_ORDER = [
  { key: 'create', label: 'Creating workflow' },
  { key: 'trigger', label: 'Adding trigger' },
  { key: 'actions', label: 'Adding actions' },
  { key: 'connect', label: 'Connecting nodes' },
  { key: 'done', label: 'Workflow ready' },
];

function getStepStatus(completedTools) {
  const steps = STEP_ORDER.map(s => ({ ...s, status: 'pending' }));

  // Determine which steps are completed based on tool calls
  const hasCreate = completedTools.some(t => t === 'create_workflow');
  const hasTrigger = completedTools.some(t => t === 'add_workflow_node');
  const hasMultipleNodes = completedTools.filter(t => t === 'add_workflow_node').length > 1;
  const hasConnect = completedTools.some(t => t === 'connect_workflow_nodes');
  const hasActivate = completedTools.some(t => t === 'activate_workflow');

  if (hasCreate) steps[0].status = 'completed';
  if (hasTrigger) steps[1].status = 'completed';
  if (hasMultipleNodes) steps[2].status = 'completed';
  if (hasConnect) steps[3].status = 'completed';
  if (hasActivate || (hasConnect && hasMultipleNodes)) steps[4].status = 'completed';

  // Find the first non-completed step and mark it as active
  const firstPending = steps.findIndex(s => s.status === 'pending');
  if (firstPending >= 0 && completedTools.length > 0) {
    steps[firstPending].status = 'active';
  }

  return steps;
}

export default function WorkflowProgressIndicator({ completedTools = [], isComplete = false }) {
  const steps = getStepStatus(completedTools);

  // Only show if at least one step is completed
  if (!completedTools.length) return null;

  // If complete, mark all steps as completed
  if (isComplete) {
    steps.forEach(s => { s.status = 'completed'; });
  }

  return (
    <div className="my-2 rounded-xl border border-gray-200 dark:border-gray-700/80 bg-white dark:bg-gray-800/60 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/80 dark:bg-gray-800/40">
        <GitBranch className="w-3.5 h-3.5 text-brand-500 dark:text-brand-400" />
        <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
          Building workflow
        </span>
      </div>

      <div className="px-3 py-2.5">
        {steps.map((step, idx) => {
          const isLast = idx === steps.length - 1;

          return (
            <div key={step.key} className="flex items-stretch gap-0">
              {/* Status icon + connector */}
              <div className="flex flex-col items-center w-5 flex-shrink-0">
                {step.status === 'completed' ? (
                  <div className="w-4 h-4 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center flex-shrink-0">
                    <Check className="w-2.5 h-2.5 text-green-600 dark:text-green-400" />
                  </div>
                ) : step.status === 'active' ? (
                  <div className="w-4 h-4 rounded-full bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center flex-shrink-0">
                    <Loader2 className="w-2.5 h-2.5 text-brand-600 dark:text-brand-400 animate-spin" />
                  </div>
                ) : (
                  <div className="w-4 h-4 rounded-full bg-gray-100 dark:bg-gray-700/60 flex items-center justify-center flex-shrink-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-500" />
                  </div>
                )}
                {!isLast && (
                  <div className={`w-px flex-1 min-h-[12px] ${
                    step.status === 'completed'
                      ? 'bg-green-200 dark:bg-green-800/60'
                      : 'bg-gray-200 dark:bg-gray-600/60'
                  }`} />
                )}
              </div>

              {/* Step label */}
              <div className="pb-1.5 pl-2 min-w-0">
                <p className={`text-xs leading-tight ${
                  step.status === 'completed'
                    ? 'text-green-700 dark:text-green-400'
                    : step.status === 'active'
                    ? 'text-gray-700 dark:text-gray-200 font-medium'
                    : 'text-gray-400 dark:text-gray-500'
                }`}>
                  {step.label}
                  {step.status === 'active' && '...'}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
